import type { SavedView, UserViewsResponse } from './saved-view'

export interface ApiColumnDefinition {
  reportTypeId: number
  columnName: string
  columnNameEnglish: string
  columnTypeCode: string
  visible: boolean
  visibleToInternalUser: boolean
  visibleToExternalUser: boolean
  id: string
}

export interface Workflow {
  id: string
  name: string
  description: string
  createdAt: string
  updatedAt: string
  columns: FieldConfiguration[]
  data: WorkflowData[]
}

export type ColumnType = "text" | "number" | "select" | "date"

export interface ColumnOption {
  id: string
  label: string
  value: string
}

export interface WorkflowData {
  id: string
  [key: string]: any
}

// New WorkflowItem interface matching the real API response
export interface WorkflowItem {
  // Core identification
  id?: string // We'll generate this from atsPlacementId or vmsId
  workflowHubId?: string
  atsPlacementId?: string
  vmsId?: string

  // Worker information
  workerEmail?: string
  workerPhone?: string
  workerLocation?: string
  workerProfileStatus?: string

  // Computed worker name for backward compatibility
  workerFullName?: string // Will be computed from first + last name

  // Assignment and management
  assignedToUserEmail?: string
  assignedToUserFullName?: string
  managerFullName?: string
  managerEmail?: string
  recruiterFullName?: string
  primaryJobOwnerFullName?: string

  // Company and client information
  clientName?: string
  supplierName?: string
  internalCompany?: string
  branch?: string

  // Job details
  jobTitle?: string
  profileType?: string
  workOrderStatus?: string

  // Legal and compliance
  eligibilityToWork?: string
  permitExpirationDate?: string

  // Financial information
  primaryPayRate?: number
  primaryBillRate?: number
  otEligible?: boolean
  otPayRate?: number
  otBillRate?: number

  // Dates
  startDate?: string
  endDate?: string
  onboardingPackageSentDate?: string
  onboardingPackageCompleteDate?: string

  // Additional fields
  duration?: number
  timesheetMethod?: string
  healthStatus?: string

  // System fields
  isDeleted?: boolean
  createdByProfileId?: number
  createdDateTime?: string
  lastModifiedByProfileId?: number
  lastModifiedDateTime?: string

  // Computed fields for UI compatibility
  stage?: WorkflowStage // Will be computed from workOrderStatus
  client?: string // Alias for clientName
  recruiter?: string // Alias for recruiterFullName
  assignedTo?: string // Alias for assignedToUserFullName

  // Allow for additional dynamic fields
  [key: string]: any
}

// Keep WorkOrder as an alias for backward compatibility during transition
export type WorkOrder = WorkflowItem

export type WorkflowStage = "onboarding" | "active" | "extension" | "offboarding" | "complete"

export interface User {
  id: string
  name: string
  count: number
  role?: string
  email?: string
  avatar?: string
}

// New interface for assigned users from the API
export interface AssignedUser {
  email: string
  fullName: string
}

// Interface for authorized user information in private columns
export interface UserReportDto {
  profileId: string
  fullName?: string
  email: string
}

// Interface for user search API response
export interface UserSearchApiResponse {
  EntityId: string
  FirstName: string
  LastName: string
  PrimaryEmail: string
}

// Response wrapper for user search results
export interface UserSearchResponse {
  '@odata.count': number
  value: UserSearchApiResponse[]
}

export interface Filters {
  [key: string]: string[] | undefined
}

// API request interface for the workflow search
export interface WorkflowSearchRequest {
  select: string
  filter: string | null
  orderBy: string | null
  top: number
  skip: number
  search: string
  facets: string[] | null
  count?: boolean
  suggesterName?: string
}

// API response interface for paginated results
export interface WorkflowApiResponse {
  data: WorkflowItem[]
  totalCount: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  facets?: Record<string, { value: string; count: number }[]>
}

// API response interface for suggestions
export interface SuggestionsApiResponse {
  value?: string[]
}

// Add new types for improved state management
export interface WorkflowState {
  // UI State
  currentStage?: WorkflowStage
  isFilterDrawerOpen: boolean
  isBulkEditModalOpen: boolean
  currentBulkEditField: string | null
  
  // Data State
  workflowItems: WorkflowItem[] // Renamed from workOrders
  columns: FieldConfiguration[]
  visibleColumns: FieldConfiguration[]
  filters: Filters
  selectedItems: string[]
  
  // Pagination State
  currentPage: number
  pageSize: number
  itemsPerPage: number | null // null = auto, number = fixed page size
  totalCount: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  
  // Loading State - consolidated into a single enum
  loadingState: LoadingState
  
  // Error State
  error: string | null
  
  // Views State
  savedViews: SavedView[]
  currentView: SavedView | null
  hasUnsavedChanges: boolean
  
  // View data from API
  viewsResponse: UserViewsResponse | null
  
  // User State
  currentUser: User

  // Configuration State
  configuration?: WorkflowConfiguration
  isLoadingConfiguration?: boolean
  configurationError?: string | null

  // Stages
  stages: StageConfiguration[]

  // Assigned Users State
  assignedUsers: AssignedUser[]
  isLoadingAssignedUsers: boolean
  assignedUsersError: string | null

  // Facets
  facets?: Record<string, { value: string; count: number }[]>

  // Filter State
  stageFilters: { stageId: string; fieldConfigurationId: string; filter: string; values?: any }[]
  globalFilters: { fieldConfigurationId: string, filter: string, values?: string }[]

  // New added workOrdersFacets
  workOrdersFacets?: Record<string, { value: string; count: number }[]>;

  // Code Values State
  codeValuesData?: Record<string, any[]>;
  isLoadingCodeValues?: boolean;
  codeValuesError?: string | null;

  // Column Widths State
  columnWidths?: Record<string, number>;
  
  // Pinned (sticky) columns per stage (stageId -> fieldConfigurationId)
  pinnedColumnsByStage?: Record<string, string | null>;

  // Client Custom Fields State
  originalConfiguration?: WorkflowConfiguration;
  activeClientFboId?: number | null;
  activeClientName?: string | null;
  cachedClientCustomFields: Map<number, ClientCustomField[]>;
  customFieldsApplied: boolean;
  isHandlingClientFilterChange: boolean;
}

export enum LoadingState {
  IDLE = 'idle',
  LOADING_COLUMNS = 'loading_columns',
  LOADING_WORK_ORDERS = 'loading_work_orders',
  LOADING_ROWS = 'loading_rows',
  UPDATING_WORK_ORDER = 'updating_work_order',
  BULK_UPDATING = 'bulk_updating',
  ERROR = 'error'
}

export type WorkflowAction =
  | { type: 'SET_CURRENT_STAGE'; payload: WorkflowStage }
  | { type: 'SET_LOADING_STATE'; payload: LoadingState }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'TOGGLE_FILTER_DRAWER' }
  | { type: 'SET_WORK_ORDERS'; payload: { data: WorkflowItem[]; totalCount: number; hasNextPage: boolean; hasPreviousPage: boolean; facets?: Record<string, { value: string; count: number }[]> } }
  | { type: 'SET_COLUMNS'; payload: { columns: FieldConfiguration[]; visibleColumns: FieldConfiguration[] } }
  | { type: 'UPDATE_VISIBLE_COLUMNS'; payload: FieldConfiguration[] }
  | { type: 'UPDATE_FILTER'; payload: { key: string; value: any } }
  | { type: 'CLEAR_FILTERS' }
  | { type: 'SET_SELECTED_ITEMS'; payload: string[] }
  | { type: 'TOGGLE_ITEM_SELECTION'; payload: string }
  | { type: 'SELECT_ALL_ITEMS'; payload: string[] }
  | { type: 'DESELECT_ALL_ITEMS' }
  | { type: 'SET_CURRENT_PAGE'; payload: number }
  | { type: 'SET_PAGE_SIZE'; payload: number }
  | { type: 'SET_ITEMS_PER_PAGE'; payload: number | null }
  | { type: 'UPDATE_WORK_ORDER_FIELD'; payload: { orderId: string; field: string; value: any } }
  | { type: 'BULK_UPDATE_WORK_ORDERS_OPTIMISTIC'; payload: { updates: { id: string; updates: Record<string, any> }[] } }
  | { type: 'REMOVE_WORK_ORDER'; payload: string }
  | { type: 'OPEN_BULK_EDIT_MODAL'; payload: string }
  | { type: 'CLOSE_BULK_EDIT_MODAL' }
  | { type: 'SET_CURRENT_VIEW'; payload: SavedView | null }
  | { type: 'SET_SAVED_VIEWS'; payload: SavedView[] }
  | { type: 'SET_UNSAVED_CHANGES'; payload: boolean }
  | { type: 'SET_VIEWS_RESPONSE'; payload: UserViewsResponse }
  | { type: 'ADD_CUSTOM_COLUMN'; payload: FieldConfiguration }
  | { type: 'DELETE_COLUMN'; payload: string }
  | { type: 'SET_CONFIGURATION'; payload: WorkflowConfiguration }
  | { type: 'SET_LOADING_CONFIGURATION'; payload: boolean }
  | { type: 'SET_CONFIGURATION_ERROR'; payload: string | null }
  | { type: 'SET_ASSIGNED_USERS'; payload: AssignedUser[] }
  | { type: 'SET_LOADING_ASSIGNED_USERS'; payload: boolean }
  | { type: 'SET_ASSIGNED_USERS_ERROR'; payload: string | null }
  | { type: 'SET_STAGE_FILTERS'; payload: { stageId: string; fieldConfigurationId: string; filter: string; values?: any }[] }
  | { type: 'SET_GLOBAL_FILTERS'; payload: { fieldConfigurationId: string, filter: string }[] }
  | { type: 'CLEAR_ALL_FILTERS' }
  | { type: 'SET_FACETS'; payload: Record<string, { value: string; count: number }[]> | undefined }
  | { type: 'SET_CODE_VALUES_DATA'; payload: Record<string, any[]> }
  | { type: 'SET_LOADING_CODE_VALUES'; payload: boolean }
  | { type: 'SET_CODE_VALUES_ERROR'; payload: string | null }
  | { type: 'SET_COLUMN_WIDTHS'; payload: Record<string, number> }
  | { type: 'SET_PINNED_COLUMN'; payload: { stageId: string; columnId: string | null } }
  | { type: 'SET_PINNED_COLUMNS_BY_STAGE'; payload: Record<string, string | null> }
  | { type: 'APPLY_CUSTOM_FIELDS'; payload: { customFields: ClientCustomField[]; clientFboId: number; clientName: string } }
  | { type: 'CLEAR_CUSTOM_FIELDS' }
  | { type: 'CACHE_CLIENT_CUSTOM_FIELDS'; payload: { clientFboId: number; customFields: ClientCustomField[] } }
  | { type: 'SET_HANDLING_CLIENT_FILTER_CHANGE'; payload: boolean }

// Add WorkflowConfiguration and related types
export interface WorkflowConfiguration {
  stageConfigurations: StageConfiguration[];
  customFieldsMetadata: CustomFieldMetadata[];
  fieldsInUse: FieldConfiguration[];
  externalSourceConfigurations?: ExternalSourceConfiguration[];
  authorizedActions?: AuthorizedAction[];
  globalFilterIds: string[];
  alwaysFetchedFieldCodes?: string[];
  // New fields for saved filters and view settings
  savedGlobalFilters?: { fieldConfigurationId: string; filter: string; values?: string }[];
  savedStageFilters?: { stageId: string; fieldConfigurationId: string; filter: string; values?: string }[];
  viewSettingsId?: string;
  itemsPerPage?: number | null; // null = auto, number = fixed page size
      columnWidths?: string; // Serialized JSON string for saved column widths
  showPendingStatusQuickFilter?: boolean;
}

export interface StageConfiguration {
  stageId: string;
  stageCode: string;
  titleEnglish: string;
  titleFrench: string;
  fieldConfigurations: FieldConfiguration[];
  order: number;
  pinnedColumnId?: string | null;
}

export interface FieldConfiguration {
  fieldConfigurationId: string;
  columnCode: string;
  columnTypeCode: string;
  columnNameEnglish: string;
  columnNameFrench: string;
  color: string;
  isEditable: boolean;
  customValuesSource: string;
  customValuesType: string;
  customValues: CustomValue[];
  order: number;
  isCustomField?: boolean;
  isFacetable?: boolean;
  isFilterable?: boolean;
  isSearchable?: boolean;
  isSortable?: boolean;
  allowMultipleSelections?: boolean;
  subColumnName?: string;
  codeValueSource?: string;
  fieldFormatting?: string; // Regex pattern for field validation
  isStaticField?: boolean; // New field to indicate if this is a static field that should be excluded from search requests
  isPrivate?: boolean; // Indicates if this field is private and restricted to specific users
  authorizedUserList?: UserReportDto[]; // List of users authorized to view this private field
  note?: string; // HTML/styled text note for the field
  displayNote?: boolean; // Whether to display the note info icon in the grid
  mappingSource?: string; // Source of field mapping (e.g., "FboCustomField")
  mappingSourceTargetColumn?: string; // Target column name in mapping source (e.g., "Dropdown_9")
  isRetainDataEnabled?: boolean; // Whether to retain column data for newly created line items with the same Assignment ID
}

export interface CustomValue {
  code: string;
  titleEnglish: string;
  titleFrench: string;
  color: string;
  isDeleted: boolean;
}

export interface CustomFieldMetadata {
  inputDataType: string;
  fieldConfigurationIds: string[];
}

// Some of these are not used in the UI at the moment, but will be in the future
export const CustomFieldInputTypeStruct = {
  Text: "Text",
  YesNo: "YesNo",
  Dropdown: "Dropdown",
  Date: "Date",
  Number: "Number",
  // Files: "Files",
  // Link: "Link",
  // LastUpdated: "LastUpdated",
  // OBLink: "ObLink",
  // FBOWorkerLink: "FboWorkerLink",
  Email: "Email",
  Checklist: "Checklist",
} as const;

export const ColumnTypeStruct = {
  Number: "Number",
  Text: "Text",
  Email: "Email",
  TextList: "TextList",
  Boolean: "Boolean",
  Decimal: "Decimal",
  Double: "Double",
  Date: "DateTime",
  Money: "Money",
  Checklist: "Checklist",
} as const;

// --- Checklist Types ---

export interface ChecklistItem {
  title: string;
  isCompleted: boolean;
  order: number;
  isDeleted: boolean;
}

export interface ChecklistData {
  items: ChecklistItem[];
  totalItems: number;
  completedItems: number;
}

// --- External Source and Action Types ---

export interface ExternalSourceConfiguration {
  titleEnglish: string;
  titleFrench: string;
  link: string;
  condition?: string;
}

export interface AuthorizedAction {
  actionCode: string;
  functionalRoleIds: number[];
  userProfileTypeIds: number[];
}

export const WorkflowHubActionCodeStruct = {
  DeleteField: "DeleteField",
  UpdateField: "UpdateField",
  CreateField: "CreateField",
} as const;

export const ExternalSourceCodeStruct = {
  Fbo: "FBO",
  Ob: "OB",
} as const;

// Client custom fields types
export interface ClientCustomField {
  DisplayName: string;
  FieldName: string;
  FieldType: string;
  SelectValues: string | null;
}

export interface ClientCustomFieldsResponse {
  ClientId: number;
  EntityTypeId: number;
  CustomFields: ClientCustomField[];
}

export enum FboEntityType {
  WorkOrder = 17
}
